import { useMemo, useRef, useEffect, useState, forwardRef } from "react";
import { MapPin, Clock } from "lucide-react";
import type { SpotResponseDto, TravelMode } from "@tabipla/shared";
import { getCategoryIconComponent, getCategoryBadgeColors } from "@/domain/constants/categories";
import { TravelTimeIndicator } from "./TravelTimeIndicator";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Constants
const HOUR_HEIGHT = 60; // px per hour
const TIME_COLUMN_WIDTH = 48; // px
const DEFAULT_START_HOUR = 8;
const DEFAULT_END_HOUR = 22;
const SPOT_WITHOUT_TIME_HEIGHT = 80; // px for spots without time

interface TimelineViewProps {
  spots: SpotResponseDto[];
  onEdit: (spot: SpotResponseDto) => void;
  onAddSpot?: (startTime?: string) => void;
  onTravelModeChange?: (spotId: string, mode: TravelMode) => void;
  onReorder?: (spotIds: string[]) => void;
  tripDate: string;
}

interface TimeSlot {
  type: "spot" | "travel" | "empty";
  spot?: SpotResponseDto;
  startMinutes: number;
  endMinutes: number;
  travelInfo?: {
    durationMinutes: number | null;
    distanceKm: number | null;
    mode: TravelMode | null;
    spotId: string;
  };
}

/**
 * Parse time string (HH:MM) to minutes from midnight
 */
function parseTimeToMinutes(time: string | null): number | null {
  if (!time) return null;
  const [hours, minutes] = time.split(":").map(Number);
  if (isNaN(hours) || isNaN(minutes)) return null;
  return hours * 60 + minutes;
}

/**
 * Format minutes to time string (HH:MM)
 */
function formatMinutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
}

/**
 * TimelineView displays spots in a vertical timeline format
 * similar to Google Calendar's day view
 */
export function TimelineView({
  spots,
  onEdit,
  onAddSpot,
  onTravelModeChange,
  onReorder,
  tripDate,
}: TimelineViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Sort spots by order
  const sortedSpots = useMemo(() => [...spots].sort((a, b) => a.order - b.order), [spots]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // DnD handlers
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id && onReorder) {
      const oldIndex = sortedSpots.findIndex((s) => s.id === active.id);
      const newIndex = sortedSpots.findIndex((s) => s.id === over.id);
      const newOrder = arrayMove(sortedSpots, oldIndex, newIndex);
      onReorder(newOrder.map((s) => s.id));
    }
  };

  const activeSpot = activeId ? sortedSpots.find((s) => s.id === activeId) : null;

  // Calculate time range based on spots
  const { startHour, endHour, hasTimeData } = useMemo(() => {
    let minTime = DEFAULT_START_HOUR * 60;
    let maxTime = DEFAULT_END_HOUR * 60;
    let hasTime = false;

    for (const spot of sortedSpots) {
      const start = parseTimeToMinutes(spot.startTime);
      const end = parseTimeToMinutes(spot.endTime);
      if (start !== null) {
        hasTime = true;
        minTime = Math.min(minTime, start);
      }
      if (end !== null) {
        hasTime = true;
        maxTime = Math.max(maxTime, end);
      }
    }

    // Round to nearest hour
    const startHour = Math.floor(minTime / 60);
    const endHour = Math.ceil(maxTime / 60);

    return { startHour, endHour: Math.max(endHour, startHour + 1), hasTimeData: hasTime };
  }, [sortedSpots]);

  // Build timeline slots (spots, travel, empty)
  const timelineSlots = useMemo((): TimeSlot[] => {
    if (!hasTimeData) {
      // If no time data, just show spots in order
      return sortedSpots.map((spot) => ({
        type: "spot" as const,
        spot,
        startMinutes: 0,
        endMinutes: 0,
      }));
    }

    const slots: TimeSlot[] = [];
    let currentMinutes = startHour * 60;

    for (let i = 0; i < sortedSpots.length; i++) {
      const spot = sortedSpots[i];
      const spotStart = parseTimeToMinutes(spot.startTime);
      const spotEnd = parseTimeToMinutes(spot.endTime);

      // Add travel time before spot (if exists)
      if (i > 0 && spot.travelDurationMinutes) {
        const travelStart = spotStart ? spotStart - spot.travelDurationMinutes : currentMinutes;
        slots.push({
          type: "travel",
          startMinutes: travelStart,
          endMinutes: spotStart ?? travelStart + spot.travelDurationMinutes,
          travelInfo: {
            durationMinutes: spot.travelDurationMinutes,
            distanceKm: spot.travelDistanceKm,
            mode: spot.travelMode,
            spotId: spot.id,
          },
        });
      }

      // Add spot
      if (spotStart !== null && spotEnd !== null) {
        slots.push({
          type: "spot",
          spot,
          startMinutes: spotStart,
          endMinutes: spotEnd,
        });
        currentMinutes = spotEnd;
      } else {
        // Spot without time - place it sequentially
        slots.push({
          type: "spot",
          spot,
          startMinutes: currentMinutes,
          endMinutes: currentMinutes + 60, // Default 1 hour
        });
        currentMinutes += 60;
      }
    }

    return slots;
  }, [sortedSpots, hasTimeData, startHour]);

  // Check if today and scroll to current time
  const isToday = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    return tripDate === today;
  }, [tripDate]);

  const currentTimeMinutes = useMemo(() => {
    if (!isToday) return null;
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  }, [isToday]);

  // Scroll to current time on mount
  useEffect(() => {
    if (currentTimeMinutes && containerRef.current) {
      const scrollPosition = ((currentTimeMinutes - startHour * 60) / 60) * HOUR_HEIGHT - 100;
      containerRef.current.scrollTop = Math.max(0, scrollPosition);
    }
  }, [currentTimeMinutes, startHour]);

  const totalHours = endHour - startHour;
  const totalHeight = totalHours * HOUR_HEIGHT;

  // If no time data, show simple list view in timeline style with drag & drop
  if (!hasTimeData) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sortedSpots.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {sortedSpots.map((spot, index) => (
              <div key={spot.id}>
                {index > 0 && (
                  <TravelTimeIndicator
                    durationMinutes={spot.travelDurationMinutes}
                    distanceKm={spot.travelDistanceKm}
                    mode={spot.travelMode}
                    showPlaceholder={true}
                    onModeChange={
                      onTravelModeChange ? (mode) => onTravelModeChange(spot.id, mode) : undefined
                    }
                  />
                )}
                <SortableSpotItem
                  spot={spot}
                  onEdit={onEdit}
                  style={{ height: SPOT_WITHOUT_TIME_HEIGHT }}
                  isDraggable={!!onReorder}
                />
              </div>
            ))}
            {sortedSpots.length === 0 && (
              <div className="py-8 text-center text-muted-foreground">スポットがありません</div>
            )}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeSpot && (
            <TimelineSpotBlock
              spot={activeSpot}
              onEdit={onEdit}
              style={{ height: SPOT_WITHOUT_TIME_HEIGHT }}
              isDragging
            />
          )}
        </DragOverlay>
      </DndContext>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={sortedSpots.map((s) => s.id)} strategy={verticalListSortingStrategy}>
        <div ref={containerRef} className="relative overflow-y-auto" style={{ maxHeight: "60vh" }}>
          {/* Timeline container */}
          <div className="relative" style={{ height: totalHeight }}>
            {/* Time grid */}
            <TimelineGrid startHour={startHour} endHour={endHour} />

            {/* Current time indicator */}
            {currentTimeMinutes !== null && (
              <CurrentTimeIndicator minutes={currentTimeMinutes} startHour={startHour} />
            )}

            {/* Empty slot click handler (behind spots) */}
            {onAddSpot && (
              <EmptySlotHandler startHour={startHour} endHour={endHour} onAddSpot={onAddSpot} />
            )}

            {/* Spots and travel indicators (above click handler) */}
            <div
              className="pointer-events-none absolute left-0 right-0 top-0 z-10"
              style={{ marginLeft: TIME_COLUMN_WIDTH }}
            >
              {timelineSlots.map((slot, index) => {
                if (slot.type === "travel" && slot.travelInfo) {
                  return (
                    <TimelineTravelBlock
                      key={`travel-${index}`}
                      startMinutes={slot.startMinutes}
                      endMinutes={slot.endMinutes}
                      travelInfo={slot.travelInfo}
                      startHour={startHour}
                      onModeChange={onTravelModeChange}
                    />
                  );
                }
                if (slot.type === "spot" && slot.spot) {
                  const top = ((slot.startMinutes - startHour * 60) / 60) * HOUR_HEIGHT;
                  const height = Math.max(
                    ((slot.endMinutes - slot.startMinutes) / 60) * HOUR_HEIGHT,
                    40 // Minimum height
                  );
                  return (
                    <SortableSpotItem
                      key={slot.spot.id}
                      spot={slot.spot}
                      onEdit={onEdit}
                      style={{
                        position: "absolute",
                        top,
                        left: 4,
                        right: 4,
                        height,
                      }}
                      isDraggable={!!onReorder}
                    />
                  );
                }
                return null;
              })}
            </div>
          </div>
        </div>
      </SortableContext>
      <DragOverlay>
        {activeSpot && (
          <TimelineSpotBlock spot={activeSpot} onEdit={onEdit} style={{ height: 60 }} isDragging />
        )}
      </DragOverlay>
    </DndContext>
  );
}

/**
 * Time grid with hour labels and grid lines
 */
function TimelineGrid({ startHour, endHour }: { startHour: number; endHour: number }) {
  const hours = [];
  for (let h = startHour; h < endHour; h++) {
    hours.push(h);
  }

  return (
    <div className="pointer-events-none absolute inset-0">
      {hours.map((hour) => {
        const top = (hour - startHour) * HOUR_HEIGHT;
        return (
          <div
            key={hour}
            className="absolute left-0 right-0 border-t border-border/50"
            style={{ top }}
          >
            <span
              className="absolute text-xs text-muted-foreground"
              style={{ left: 4, top: 2, width: TIME_COLUMN_WIDTH - 8 }}
            >
              {hour}:00
            </span>
          </div>
        );
      })}
      {/* Vertical separator */}
      <div
        className="absolute bottom-0 top-0 border-r border-border/50"
        style={{ left: TIME_COLUMN_WIDTH }}
      />
    </div>
  );
}

/**
 * Current time indicator (red line)
 */
function CurrentTimeIndicator({ minutes, startHour }: { minutes: number; startHour: number }) {
  const top = ((minutes - startHour * 60) / 60) * HOUR_HEIGHT;

  return (
    <div className="pointer-events-none absolute left-0 right-0 z-10" style={{ top }}>
      <div className="flex items-center">
        <div className="h-3 w-3 rounded-full bg-red-500" />
        <div className="h-0.5 flex-1 bg-red-500" />
      </div>
    </div>
  );
}

/**
 * Empty slot click handler with visual feedback
 */
function EmptySlotHandler({
  startHour,
  endHour,
  onAddSpot,
}: {
  startHour: number;
  endHour: number;
  onAddSpot: (startTime?: string) => void;
}) {
  const hours = [];
  for (let h = startHour; h < endHour; h++) {
    hours.push(h);
  }

  const handleClick = (hour: number, isHalfHour: boolean) => {
    const minutes = hour * 60 + (isHalfHour ? 30 : 0);
    onAddSpot(formatMinutesToTime(minutes));
  };

  return (
    <div className="absolute inset-0" style={{ marginLeft: TIME_COLUMN_WIDTH }}>
      {hours.map((hour) => (
        <div key={hour}>
          {/* First half (XX:00) */}
          <button
            type="button"
            onClick={() => handleClick(hour, false)}
            className="group absolute left-0 right-0 cursor-pointer"
            style={{
              top: (hour - startHour) * HOUR_HEIGHT,
              height: HOUR_HEIGHT / 2,
            }}
            aria-label={`${hour}:00にスポットを追加`}
          >
            <div className="flex h-full items-center justify-center opacity-0 transition-opacity group-hover:opacity-100 group-active:opacity-100">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
                <span className="text-lg leading-none">+</span>
              </div>
            </div>
          </button>
          {/* Second half (XX:30) */}
          <button
            type="button"
            onClick={() => handleClick(hour, true)}
            className="group absolute left-0 right-0 cursor-pointer"
            style={{
              top: (hour - startHour) * HOUR_HEIGHT + HOUR_HEIGHT / 2,
              height: HOUR_HEIGHT / 2,
            }}
            aria-label={`${hour}:30にスポットを追加`}
          >
            <div className="flex h-full items-center justify-center opacity-0 transition-opacity group-hover:opacity-100 group-active:opacity-100">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
                <span className="text-lg leading-none">+</span>
              </div>
            </div>
          </button>
        </div>
      ))}
    </div>
  );
}

/**
 * Sortable wrapper for spot items
 */
function SortableSpotItem({
  spot,
  onEdit,
  style,
  isDraggable,
}: {
  spot: SpotResponseDto;
  onEdit: (spot: SpotResponseDto) => void;
  style?: React.CSSProperties;
  isDraggable?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: spot.id,
    disabled: !isDraggable,
  });

  const sortableStyle: React.CSSProperties = {
    ...style,
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TimelineSpotBlock
      ref={setNodeRef}
      spot={spot}
      onEdit={onEdit}
      style={sortableStyle}
      isDragging={isDragging}
      dragProps={isDraggable ? { ...attributes, ...listeners } : undefined}
    />
  );
}

/**
 * Spot block in timeline
 */
const TimelineSpotBlock = forwardRef<
  HTMLDivElement,
  {
    spot: SpotResponseDto;
    onEdit: (spot: SpotResponseDto) => void;
    style?: React.CSSProperties;
    isDragging?: boolean;
    dragProps?: React.HTMLAttributes<HTMLDivElement>;
  }
>(function TimelineSpotBlock({ spot, onEdit, style, isDragging, dragProps }, ref) {
  const CategoryIcon = getCategoryIconComponent(spot.category);
  const bgColors = getCategoryBadgeColors(spot.category);

  return (
    <div
      ref={ref}
      className={`pointer-events-auto w-full cursor-pointer overflow-hidden rounded-lg border text-left transition-all active:scale-[0.98] ${
        isDragging ? "cursor-grabbing shadow-lg ring-2 ring-primary/50" : "hover:shadow-md"
      } ${bgColors}`}
      style={style}
      onClick={() => onEdit(spot)}
      {...dragProps}
    >
      <div className="flex h-full flex-col p-2">
        {/* Header */}
        <div className="flex items-start gap-2">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded">
            {CategoryIcon ? <CategoryIcon className="h-4 w-4" /> : <MapPin className="h-4 w-4" />}
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="truncate text-sm font-medium leading-tight">{spot.name}</h4>
            {spot.startTime && spot.endTime && (
              <p className="flex items-center gap-1 text-xs opacity-75">
                <Clock className="h-3 w-3" />
                {spot.startTime} - {spot.endTime}
              </p>
            )}
          </div>
        </div>
        {/* Description (if space allows) */}
        {spot.description && (
          <p className="mt-1 line-clamp-2 text-xs opacity-75">{spot.description}</p>
        )}
      </div>
    </div>
  );
});

/**
 * Travel time block between spots
 */
function TimelineTravelBlock({
  startMinutes,
  endMinutes,
  travelInfo,
  startHour,
  onModeChange,
}: {
  startMinutes: number;
  endMinutes: number;
  travelInfo: {
    durationMinutes: number | null;
    distanceKm: number | null;
    mode: TravelMode | null;
    spotId: string;
  };
  startHour: number;
  onModeChange?: (spotId: string, mode: TravelMode) => void;
}) {
  const top = ((startMinutes - startHour * 60) / 60) * HOUR_HEIGHT;
  const height = Math.max(((endMinutes - startMinutes) / 60) * HOUR_HEIGHT, 24);

  return (
    <div
      className="absolute left-1 right-1 flex items-center justify-center"
      style={{ top, height }}
    >
      <TravelTimeIndicator
        durationMinutes={travelInfo.durationMinutes}
        distanceKm={travelInfo.distanceKm}
        mode={travelInfo.mode}
        onModeChange={onModeChange ? (mode) => onModeChange(travelInfo.spotId, mode) : undefined}
      />
    </div>
  );
}
